/**
 * URLs amigables para entidades WhatsApp.
 *
 * Patrón Chatwoot/Linear/GitHub: la URL usa `<prefijo>-<display_id>` (entero
 * secuencial por tenant), no el UUID. El UUID sigue siendo la PK interna —
 * solo cambia lo que ve el usuario.
 *
 *   /chats/inbox/C-4521          ← conversación
 *   /chats/inbox?channel=L-1     ← línea / inbox
 *   /chats/inbox/<uuid>          ← soportado por compat (links viejos)
 */

/** Construye el segmento de URL a partir del display_id de una conversación. */
export function conversationSlug(displayId: number | null | undefined): string | null {
  if (displayId == null || !Number.isFinite(displayId)) return null;
  return `C-${displayId}`;
}

export interface ParsedConversationParam {
  displayId: number | null;
  uuid: string | null;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Acepta cualquiera de los 3 formatos posibles en la URL:
 *   - `C-4521`  → { displayId: 4521 }
 *   - `4521`    → { displayId: 4521 } (tolerante, por si alguien lo escribe sin prefijo)
 *   - `<uuid>`  → { uuid } (compat con links viejos / shares externos)
 */
export function parseConversationParam(raw: string | undefined | null): ParsedConversationParam {
  return parseSlugParam(raw, 'C');
}

/** Slug para una línea/inbox: `L-1`, `L-2`, … */
export function lineSlug(displayId: number | null | undefined): string | null {
  if (displayId == null || !Number.isFinite(displayId)) return null;
  return `L-${displayId}`;
}

export function parseLineParam(raw: string | undefined | null): ParsedConversationParam {
  return parseSlugParam(raw, 'L');
}

/**
 * Parser genérico: acepta `<prefijo>-<n>`, `<n>` o UUID. Usado por
 * parseConversationParam ('C') y parseLineParam ('L').
 */
function parseSlugParam(
  raw: string | undefined | null,
  prefix: 'C' | 'L',
): ParsedConversationParam {
  if (!raw) return { displayId: null, uuid: null };
  const s = raw.trim();
  if (UUID_RE.test(s)) return { displayId: null, uuid: s };
  const lower = prefix.toLowerCase();
  const stripped =
    s.startsWith(`${prefix}-`) || s.startsWith(`${lower}-`) ? s.slice(2) : s;
  const n = Number(stripped);
  if (Number.isFinite(n) && Number.isInteger(n) && n > 0) {
    return { displayId: n, uuid: null };
  }
  return { displayId: null, uuid: null };
}
